/**
 * Milestone 1–3 — WebGL underlay for batched footprint cell fills (M1),
 * wick/body batches (M2), and atlas cell-volume text sprites (M3).
 * Phone-safe WebGL1.
 * Feature flag: localStorage.footprintCellGL === '0' forces 2D.
 */

export function isFootprintCellGLEnabled() {
  try {
    return localStorage.footprintCellGL !== '0'
  } catch (_) {
    return true
  }
}

// ── VIEW TRANSFORM ──────────────────────────────────────────────────────
// a_pos arrives in FINAL SCREEN PIXELS, which is why every pan used to be a
// CPU job: moving the view meant rewriting every vertex and re-uploading the
// whole buffer, once per frame, however many cells there were. The GPU was
// doing one divide and nothing else.
//
// These three uniforms make the view a transform instead of a rebuild:
//
//   p = (a_pos - u_anchor) * u_scale + u_anchor + u_translate
//
// A pan is then u_translate alone — two floats, no buffer traffic, O(1) at
// any cell count, and with u_scale at 1 the sprite path stays pixel-exact so
// text does not soften. Zoom uses u_scale about u_anchor; that DOES stretch
// sprites, so a caller that cares can keep rebuilding for zoom and use the
// translate path for pan only.
//
// Default is identity, so nothing changes until a caller sets it.
const VERT_SRC = `
attribute vec2 a_pos;
uniform vec2 u_res;
uniform vec2 u_translate;
uniform vec2 u_scale;
uniform vec2 u_anchor;
void main() {
  vec2 p = (a_pos - u_anchor) * u_scale + u_anchor + u_translate;
  vec2 clip = vec2(p.x / u_res.x * 2.0 - 1.0, 1.0 - p.y / u_res.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
}
`

const FRAG_SRC = `
precision mediump float;
uniform vec4 u_color;
void main() {
  gl_FragColor = u_color;
}
`

// M3 — textured quads from atlas page canvases (device-px sx/sy/sw/sh on 1024²).
const TEX_VERT_SRC = `
attribute vec2 a_pos;
attribute vec2 a_uv;
uniform vec2 u_res;
uniform vec2 u_translate;
uniform vec2 u_scale;
uniform vec2 u_anchor;
varying vec2 v_uv;
void main() {
  vec2 p = (a_pos - u_anchor) * u_scale + u_anchor + u_translate;
  vec2 clip = vec2(p.x / u_res.x * 2.0 - 1.0, 1.0 - p.y / u_res.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  v_uv = a_uv;
}
`

const TEX_FRAG_SRC = `
precision mediump float;
uniform sampler2D u_tex;
uniform float u_alpha;
varying vec2 v_uv;
void main() {
  vec4 t = texture2D(u_tex, v_uv);
  // Premultiply by LOD cell alpha for ONE, ONE_MINUS_SRC_ALPHA blend.
  float a = t.a * u_alpha;
  gl_FragColor = vec4(t.rgb * u_alpha, a);
}
`

function compile(gl, type, src) {
  const s = gl.createShader(type)
  gl.shaderSource(s, src)
  gl.compileShader(s)
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    gl.deleteShader(s)
    return null
  }
  return s
}

function linkProgram(gl, vs, fs) {
  const p = gl.createProgram()
  gl.attachShader(p, vs)
  gl.attachShader(p, fs)
  gl.linkProgram(p)
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    gl.deleteProgram(p)
    return null
  }
  return p
}

/** Parse css color → [r,g,b,a] 0..1. Supports #rgb #rrggbb rgb() rgba(). */
function parseCssColor(str, cache) {
  let hit = cache.get(str)
  if (hit) return hit
  let r = 0, g = 0, b = 0, a = 1
  const s = (str || '').trim()
  if (s.charCodeAt(0) === 35) { // #
    const h = s.slice(1)
    if (h.length === 3) {
      r = parseInt(h[0] + h[0], 16) / 255
      g = parseInt(h[1] + h[1], 16) / 255
      b = parseInt(h[2] + h[2], 16) / 255
    } else if (h.length >= 6) {
      r = parseInt(h.slice(0, 2), 16) / 255
      g = parseInt(h.slice(2, 4), 16) / 255
      b = parseInt(h.slice(4, 6), 16) / 255
    }
  } else {
    const m = s.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/i)
    if (m) {
      r = (+m[1]) / 255
      g = (+m[2]) / 255
      b = (+m[3]) / 255
      if (m[4] !== undefined) a = +m[4]
    }
  }
  hit = [r, g, b, a]
  cache.set(str, hit)
  return hit
}

/**
 * @param {HTMLCanvasElement} canvas
 * @returns {null | {
 *   ok: boolean,
 *   resize: (cssW: number, cssH: number, dpr: number) => void,
 *   setCssSize: (cssW: number, cssH: number) => void,
 *   clear: () => void,
 *   clearCssRect: (x: number, y: number, w: number, h: number) => void,
 *   shiftCssRect: (x: number, y: number, w: number, h: number, dxCss: number) => boolean,
 *   beginFrame: () => void,
 *   getContext: () => WebGLRenderingContext | null,
 *   beginHeatLayer: () => boolean,
 *   commitHeatLayer: () => void,
 *   blitHeatLayer: (dxCss?: number, scaleY?: number, tyCss?: number) => boolean,
 *   hasHeatSnapshot: boolean,
 *   drawColorBatches: (fillMap: Map<string, number[]>, alpha: number) => boolean,
 *   drawHeatBatches: (fillMap: Map<string, number[]>, alpha: number) => boolean,
 *   drawWickBatch: (wb: Array) => boolean,
 *   drawSpriteBatch: (sb: Array, alpha: number) => boolean,
 * }}
 */
// resize() probe counters, shared by every controller. One object for the
// process; window.__glResize points at it (no per-call global lookups).
const _glProbe = { calls: 0, changes: 0, last: '' }
if (typeof window !== 'undefined') window.__glResize = _glProbe

export function createFootprintCellGL(canvas) {
  if (!canvas) return null

  let gl = null
  try {
    gl = canvas.getContext('webgl', {
      // antialias:false IS LOAD-BEARING, not a perf tweak.
      // Everything this context draws is an axis-aligned quad, so MSAA buys
      // nothing on the interiors — and on SHARED EDGES it actively breaks
      // them. Two quads meeting at a fractional coordinate each get partial
      // coverage under MSAA, and with ONE / ONE_MINUS_SRC_ALPHA the two
      // partials do not sum back to full: the joint shows as a translucent
      // hairline. With MSAA off the top-left fill rule gives that pixel to
      // exactly one triangle — no gap, no double-blend.
      // That is what lets the heat layer paint at true sub-pixel x instead
      // of snapping every run to the device grid, which was the last thing
      // quantising the ribbon's motion. It also removes an MSAA resolve and
      // its memory from every frame.
      // Row edges already sat at fractional y (yOfRow is r*RH, RH is not an
      // integer), so this should if anything sharpen horizontal joints too.
      antialias: false,
      alpha: true,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
    })
  } catch (_) {
    gl = null
  }
  if (!gl) return null

  const vs = compile(gl, gl.VERTEX_SHADER, VERT_SRC)
  const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG_SRC)
  if (!vs || !fs) return null
  const prog = linkProgram(gl, vs, fs)
  gl.deleteShader(vs)
  gl.deleteShader(fs)
  if (!prog) return null

  const aPos = gl.getAttribLocation(prog, 'a_pos')
  const uRes = gl.getUniformLocation(prog, 'u_res')
  const uColor = gl.getUniformLocation(prog, 'u_color')
  const uTranslate = gl.getUniformLocation(prog, 'u_translate')
  const uScale = gl.getUniformLocation(prog, 'u_scale')
  const uAnchor = gl.getUniformLocation(prog, 'u_anchor')

  // M3 textured program
  const tvs = compile(gl, gl.VERTEX_SHADER, TEX_VERT_SRC)
  const tfs = compile(gl, gl.FRAGMENT_SHADER, TEX_FRAG_SRC)
  let texProg = null
  let aTexPos = -1, aTexUv = -1, uTexRes = null, uTex = null, uTexAlpha = null
  let uTexTranslate = null, uTexScale = null, uTexAnchor = null
  // Current view transform, CSS px. Identity until setViewTransform is called.
  const xform = { tx: 0, ty: 0, sx: 1, sy: 1, ax: 0, ay: 0 }
  if (tvs && tfs) {
    texProg = linkProgram(gl, tvs, tfs)
    gl.deleteShader(tvs)
    gl.deleteShader(tfs)
  } else {
    if (tvs) gl.deleteShader(tvs)
    if (tfs) gl.deleteShader(tfs)
  }
  if (texProg) {
    aTexPos = gl.getAttribLocation(texProg, 'a_pos')
    aTexUv = gl.getAttribLocation(texProg, 'a_uv')
    uTexRes = gl.getUniformLocation(texProg, 'u_res')
    uTex = gl.getUniformLocation(texProg, 'u_tex')
    uTexAlpha = gl.getUniformLocation(texProg, 'u_alpha')
    uTexTranslate = gl.getUniformLocation(texProg, 'u_translate')
    uTexScale = gl.getUniformLocation(texProg, 'u_scale')
    uTexAnchor = gl.getUniformLocation(texProg, 'u_anchor')
  }

  // WebGL uniforms default to ZERO, so u_scale would come up (0,0) and
  // collapse every vertex onto the anchor. These must be pushed wherever
  // u_res is pushed — there is no "leave it alone" state.
  const applyXform = (uT, uS, uA) => {
    if (uT) gl.uniform2f(uT, xform.tx, xform.ty)
    if (uS) gl.uniform2f(uS, xform.sx, xform.sy)
    if (uA) gl.uniform2f(uA, xform.ax, xform.ay)
  }

  const buf = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, buf)
  // Bytes currently ALLOCATED in buf. The solid path reserves by doubling
  // and then only ever bufferSubData's into it, so the store is reallocated
  // on growth alone instead of once per colour per frame.
  let bufBytes = 0
  const texBuf = gl.createBuffer()

  let dead = false
  let cssW = 0
  let cssH = 0
  let styleW = ''
  let styleH = ''
  // resize() input memo (chart-perf) — NaN so the first call always runs.
  let lastInW = NaN, lastInH = NaN, lastInDpr = NaN
  /** @type {Float32Array} */
  let verts = new Float32Array(6 * 2 * 256) // 256 rects worth
  /** @type {Float32Array} interleaved pos+uv (x,y,u,v) * 6 verts/sprite */
  let texVerts = new Float32Array(6 * 4 * 128)
  const colorCache = new Map()
  // Reused across drawWickBatch frames to avoid Map alloc churn
  /** @type {Map<string, number[]>} */
  const wickCoreMap = new Map()
  /** @type {Map<string, number[]>} */
  const wickBodyMap = new Map()
  // page canvas → { tex, epoch }; WeakMap so eviction/GC drops GPU textures
  /** @type {WeakMap<HTMLCanvasElement, { tex: WebGLTexture, epoch: number }>} */
  const texCache = typeof WeakMap !== 'undefined' ? new WeakMap() : new Map()

  // S2 — offscreen heat layer (FBO). Freeze blit keeps cells live while heat stays put.
  let heatFbo = null
  let heatTex = null
  let heatDw = 0
  let heatDh = 0
  let heatReady = false
  let heatPassActive = false
  /** Scratch texture for shiftCssRect (flow-band scroll blit). */
  let shiftTex = null

  const onLost = (e) => {
    e.preventDefault()
    dead = true
    try { if (shiftTex && gl) gl.deleteTexture(shiftTex) } catch (_) {}
    shiftTex = null
  }
  const onRestored = () => {
    // M1: do not attempt restore — mark dead so caller falls back to 2D.
    dead = true
  }
  canvas.addEventListener('webglcontextlost', onLost, false)
  canvas.addEventListener('webglcontextrestored', onRestored, false)

  // GROWS WITHOUT LOSING CONTENT. This used to allocate a bare replacement,
  // which was safe while drawFlatRects packed one colour from index 0 and
  // uploaded immediately. The solid path now stages every colour of a frame
  // before its single upload, so a grow part-way through a batch would have
  // silently dropped everything already staged — colours vanishing from a
  // frame the moment the batch outgrew the buffer, and only at scale.
  function ensureVerts(floatsNeeded) {
    if (verts.length >= floatsNeeded) return
    let n = verts.length
    while (n < floatsNeeded) n *= 2
    const next = new Float32Array(n)
    next.set(verts)
    verts = next
  }

  function ensureTexVerts(floatsNeeded) {
    if (texVerts.length >= floatsNeeded) return
    let n = texVerts.length
    while (n < floatsNeeded) n *= 2
    texVerts = new Float32Array(n)
  }

  /**
   * Upload atlas page canvas when missing or epoch dirty.
   * UNPACK_FLIP_Y_WEBGL = true → UV with canvas top-left as high-v:
   *   u0 = sx/1024, u1 = (sx+sw)/1024
   *   vTop = 1 - sy/1024, vBot = 1 - (sy+sh)/1024
   * Epoch is mirrored onto the canvas as _atlasEpoch (see chartUtils).
   */
  function ensurePageTexture(pgCanvas) {
    if (!pgCanvas || !(pgCanvas.width > 0)) {
      try { if (texCache.delete) texCache.delete(pgCanvas) } catch (_) {}
      return null
    }
    const epoch = (pgCanvas._atlasEpoch || 0) | 0
    let entry = texCache.get(pgCanvas)
    if (entry && entry.epoch === epoch) return entry.tex
    let tex = entry && entry.tex
    if (!tex) {
      tex = gl.createTexture()
      if (!tex) return null
    }
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true)
    try {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, pgCanvas)
    } catch (_) {
      try { gl.deleteTexture(tex) } catch (_) {}
      try { if (texCache.delete) texCache.delete(pgCanvas) } catch (_) {}
      return null
    }
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    texCache.set(pgCanvas, { tex, epoch })
    return tex
  }

  function destroyHeatTarget() {
    if (!gl) return
    try { if (heatFbo) gl.deleteFramebuffer(heatFbo) } catch (_) {}
    try { if (heatTex) gl.deleteTexture(heatTex) } catch (_) {}
    heatFbo = null
    heatTex = null
    heatDw = 0
    heatDh = 0
    heatReady = false
    heatPassActive = false
  }

  function ensureHeatTarget(dw, dh) {
    if (dead || !gl) return false
    dw = Math.max(1, dw | 0)
    dh = Math.max(1, dh | 0)
    if (heatFbo && heatTex && heatDw === dw && heatDh === dh) return true
    destroyHeatTarget()
    heatTex = gl.createTexture()
    heatFbo = gl.createFramebuffer()
    if (!heatTex || !heatFbo) { destroyHeatTarget(); return false }
    gl.bindTexture(gl.TEXTURE_2D, heatTex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, dw, dh, 0, gl.RGBA, gl.UNSIGNED_BYTE, null)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.bindFramebuffer(gl.FRAMEBUFFER, heatFbo)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, heatTex, 0)
    const ok = gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    gl.bindTexture(gl.TEXTURE_2D, null)
    if (!ok) { destroyHeatTarget(); return false }
    heatDw = dw
    heatDh = dh
    return true
  }

  function beginSolidPass() {
    gl.disable(gl.DEPTH_TEST)
    gl.enable(gl.BLEND)
    // premultipliedAlpha: true → src * srcA + dst * (1 - srcA)
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
    gl.useProgram(prog)
    gl.bindBuffer(gl.ARRAY_BUFFER, buf)
    gl.enableVertexAttribArray(aPos)
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0)
    // M3 may have left a_uv enabled on another location — disable if distinct
    if (aTexUv >= 0 && aTexUv !== aPos) gl.disableVertexAttribArray(aTexUv)
    if (aTexPos >= 0 && aTexPos !== aPos) gl.disableVertexAttribArray(aTexPos)
    gl.uniform2f(uRes, cssW || 1, cssH || 1)
    applyXform(uTranslate, uScale, uAnchor)
  }

  // ── SOLID BATCH: ONE UPLOAD PER FRAME, N CHEAP DRAWS ────────────────────
  // drawFlatRects used to be self-contained: pack this color's rects, then
  // gl.bufferData + uniform4f + drawArrays. drawColorBatches called it once
  // PER COLOR, so a heat frame with the 33-stop ramp issued 30–60 bufferData
  // calls — and bufferData REALLOCATES the store every time rather than
  // writing into it. That realloc, not the rasterization, is what made the
  // GL path lose to a colour-batched 2D path: a profile put every 2D raster
  // primitive combined at 3.8% of the trace, so there was under 4% to win
  // and the submission path was spending more than that.
  //
  // Split into stage-then-flush. Every colour's rects are packed contiguously
  // into one staging array, uploaded with a SINGLE bufferSubData into a
  // persistent buffer, and then drawn with one uniform4f + drawArrays per
  // colour at its own vertex offset. Draw calls stay at N; buffer traffic
  // drops from N reallocs to one write into an already-allocated store, and
  // uniform+draw with no buffer traffic is cheap.
  //
  // Colour stays a UNIFORM deliberately. Moving it into the vertex data
  // would collapse this to a single draw call, but it triples upload volume
  // (2 floats per vertex becomes 6) and an index buffer to claw that back
  // caps a frame at 16383 quads on WebGL1's UNSIGNED_SHORT unless
  // OES_element_index_uint is present. Not worth it to remove N cheap draws.
  //
  // CALLERS MUST FLUSH — push* only stages. Every entry point that stages
  // has to end with flushSolid() or the frame silently draws nothing.
  let solidGroups = []        // { color, aMul, first, count } — vertex units
  let solidVertN = 0          // verts staged this batch
  function resetSolid() {
    solidGroups.length = 0
    solidVertN = 0
  }
  function pushSolidRects(color, q, aMul) {
    const n = q.length
    if (n < 4) return
    const rects = (n / 4) | 0
    const first = solidVertN
    ensureVerts((solidVertN + rects * 6) * 2)
    let o = solidVertN * 2
    for (let i = 0; i < rects * 4; i += 4) {
      const x = q[i], y = q[i + 1], w = q[i + 2], h = q[i + 3]
      const x2 = x + w, y2 = y + h
      verts[o++] = x;  verts[o++] = y
      verts[o++] = x2; verts[o++] = y
      verts[o++] = x;  verts[o++] = y2
      verts[o++] = x;  verts[o++] = y2
      verts[o++] = x2; verts[o++] = y
      verts[o++] = x2; verts[o++] = y2
    }
    solidVertN += rects * 6
    solidGroups.push({ color, aMul, first, count: rects * 6 })
  }
  function flushSolid() {
    if (solidVertN === 0) { resetSolid(); return }
    const bytes = solidVertN * 2 * 4
    if (bytes > bufBytes) {
      // Grow by doubling and ALLOCATE ONLY — passing a size rather than data
      // reserves the store without an upload, so the bufferSubData below is
      // the single write. Growth is rare; steady state never reallocates.
      let nb = bufBytes || 4096
      while (nb < bytes) nb *= 2
      gl.bufferData(gl.ARRAY_BUFFER, nb, gl.DYNAMIC_DRAW)
      bufBytes = nb
    }
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, verts.subarray(0, solidVertN * 2))
    for (let i = 0; i < solidGroups.length; i++) {
      const g = solidGroups[i]
      const rgba = parseCssColor(g.color, colorCache)
      const ca = rgba[3] * g.aMul
      gl.uniform4f(uColor, rgba[0] * ca, rgba[1] * ca, rgba[2] * ca, ca)
      gl.drawArrays(gl.TRIANGLES, g.first, g.count)
    }
    resetSolid()
  }

  const controller = {
    get ok() { return !dead && !!gl },
    get lost() { return dead },

    resize(w, h, dpr) {
      if (dead || !gl) return
      // (chart-perf) INPUT-LEVEL EARLY-OUT. Both callers (FPCC's cell GL and
      // LiquidityHeatLayer's own GL) hit this on every paint, i.e. ~2x per
      // frame forever, with the same whole-pixel inputs. Bail before any DOM
      // getter or probe work. lastIn* reset on a real change below, and a
      // fresh controller starts at NaN, so the first call always sizes.
      if (w === lastInW && h === lastInH && dpr === lastInDpr) { _glProbe.calls++; return }
      const dw = Math.max(1, Math.round(w * dpr))
      const dh = Math.max(1, Math.round(h * dpr))
      const sizeChanged = canvas.width !== dw || canvas.height !== dh
      lastInW = w; lastInH = h; lastInDpr = dpr
      // PROBE (cheap): module-level counters, exposed as window.__glResize.
      // changes ~= calls means the size really is flipping; the logged
      // from->to pair names the two values doing it.
      _glProbe.calls++
      if (sizeChanged) {
        _glProbe.changes++
        const pair = `${canvas.width}x${canvas.height} -> ${dw}x${dh}`
        if (pair !== _glProbe.last) { _glProbe.last = pair; console.log('[GLRESIZE]', pair, 'call#' + _glProbe.calls) }
      }
      cssW = w
      cssH = h
      // Early-out: own-GL heat + FPCC call this every paint. Unchanged
      // dw/dh still paid viewport/bind churn; half-pixel CSS usually
      // rounds to the same device size (see __sh probe).
      if (!sizeChanged) return
      canvas.width = dw
      canvas.height = dh
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.viewport(0, 0, dw, dh)
      // INVALIDATE ONLY — DO NOT ALLOCATE HERE.
      // This used to call ensureHeatTarget(), which builds a full-canvas RGBA
      // texture + framebuffer and runs checkFramebufferStatus. The heat FBO is
      // used by exactly one path — beginHeatLayer/blitHeatLayer, the shared
      // scene — and beginHeatLayer ALREADY calls ensureHeatTarget itself, so
      // doing it here was duplication even for that path.
      //
      // For the path actually in use it was pure waste, charged every frame.
      // LiquidityHeatLayer's own-GL branch calls resize() on every paint and
      // never calls beginHeatLayer, so `!heatFbo` kept firing: ensureHeatTarget
      // failing anywhere calls destroyHeatTarget(), which nulls heatFbo, so the
      // next frame retried, and the next. A profile put resize() at 714ms of
      // SELF time — 22.9% of the trace, second only to the layout-shift
      // synthetic — with checkFramebufferStatus a further 2.1% beside it.
      //
      // Allocation belongs at the point of use. heatReady still clears so a
      // stale snapshot can never be blitted after a size change.
      if (sizeChanged) heatReady = false
    },

    setCssSize(w, h) {
      const sw = `${w}px`
      const sh = `${h}px`
      if (sw !== styleW) { canvas.style.width = sw; styleW = sw }
      if (sh !== styleH) { canvas.style.height = sh; styleH = sh }
      cssW = w
      cssH = h
    },

    clear() {
      if (dead || !gl) return
      const useHeat = heatPassActive && heatFbo
      const dw = useHeat ? heatDw : canvas.width
      const dh = useHeat ? heatDh : canvas.height
      gl.bindFramebuffer(gl.FRAMEBUFFER, useHeat ? heatFbo : null)
      gl.viewport(0, 0, dw, dh)
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
    },

    /**
     * Clear a CSS-pixel rect to transparent on the default FB.
     * Phone WebKit often composites this WebGL canvas ABOVE the sibling 2D
     * axis-backing canvas; without punching the y-axis strip clear, closed
     * footprint cells paint over the price axis. Live layer already
     * clearRect's the same strip on its 2D canvas — this is that fix for GL.
     * @param {number} x
     * @param {number} y
     * @param {number} w
     * @param {number} h
     */
    clearCssRect(x, y, w, h) {
      if (dead || !gl) return
      if (!(w > 0) || !(h > 0) || !(cssW > 0) || !(cssH > 0)) return
      const useHeat = heatPassActive && heatFbo
      const dw = useHeat ? heatDw : canvas.width
      const dh = useHeat ? heatDh : canvas.height
      const sx = Math.max(0, Math.floor((x / cssW) * dw))
      // WebGL scissor origin is bottom-left; CSS y is top-left.
      const syCss = Math.max(0, Math.floor((y / cssH) * dh))
      const sw = Math.min(dw - sx, Math.ceil((w / cssW) * dw))
      const sh = Math.min(dh - syCss, Math.ceil((h / cssH) * dh))
      if (!(sw > 0) || !(sh > 0)) return
      const sy = dh - syCss - sh
      gl.bindFramebuffer(gl.FRAMEBUFFER, useHeat ? heatFbo : null)
      gl.viewport(0, 0, dw, dh)
      gl.enable(gl.SCISSOR_TEST)
      gl.scissor(sx, sy, sw, sh)
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
      gl.disable(gl.SCISSOR_TEST)
    },

    /**
     * Integer CSS-pixel band shift on the active FB (default or heat FBO).
     * copyTexImage2D → scissor-clear source → textured quad at dest.
     * Opaque copy (blend ONE,ZERO). CLAMP_TO_EDGE + NEAREST.
     * @returns {boolean}
     */
    shiftCssRect(x, y, w, h, dxCss) {
      if (dead || !gl || !texProg) return false
      if (!(w > 0) || !(h > 0) || !(cssW > 0) || !(cssH > 0)) return false
      if (!dxCss) return true // no-op success
      const useHeat = heatPassActive && heatFbo
      const dw = useHeat ? heatDw : canvas.width
      const dh = useHeat ? heatDh : canvas.height
      if (!(dw > 0) || !(dh > 0)) return false
      const sx = Math.max(0, Math.floor((x / cssW) * dw))
      const syCss = Math.max(0, Math.floor((y / cssH) * dh))
      const sw = Math.min(dw - sx, Math.ceil((w / cssW) * dw))
      const sh = Math.min(dh - syCss, Math.ceil((h / cssH) * dh))
      if (!(sw > 0) || !(sh > 0)) return false
      const sy = dh - syCss - sh
      try {
        gl.bindFramebuffer(gl.FRAMEBUFFER, useHeat ? heatFbo : null)
        gl.viewport(0, 0, dw, dh)
        if (!shiftTex) {
          shiftTex = gl.createTexture()
          if (!shiftTex) return false
        }
        gl.activeTexture(gl.TEXTURE0)
        gl.bindTexture(gl.TEXTURE_2D, shiftTex)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
        // Read FB rect into shiftTex (GL lower-left = CSS bottom → v=0).
        gl.copyTexImage2D(gl.TEXTURE_2D, 0, gl.RGBA, sx, sy, sw, sh, 0)
        // Vacate source band (copy semantics).
        gl.enable(gl.SCISSOR_TEST)
        gl.scissor(sx, sy, sw, sh)
        gl.clearColor(0, 0, 0, 0)
        gl.clear(gl.COLOR_BUFFER_BIT)
        // Opaque blit of shifted content; scissor keeps dest inside the band.
        const blendOn = gl.isEnabled(gl.BLEND)
        gl.disable(gl.BLEND)
        gl.disable(gl.DEPTH_TEST)
        gl.useProgram(texProg)
        gl.bindBuffer(gl.ARRAY_BUFFER, texBuf)
        gl.enableVertexAttribArray(aTexPos)
        gl.enableVertexAttribArray(aTexUv)
        gl.vertexAttribPointer(aTexPos, 2, gl.FLOAT, false, 16, 0)
        gl.vertexAttribPointer(aTexUv, 2, gl.FLOAT, false, 16, 8)
        if (aPos >= 0 && aPos !== aTexPos && aPos !== aTexUv) gl.disableVertexAttribArray(aPos)
        gl.uniform2f(uTexRes, cssW || 1, cssH || 1)
        applyXform(uTexTranslate, uTexScale, uTexAnchor)
        gl.uniform1i(uTex, 0)
        gl.uniform1f(uTexAlpha, 1)
        gl.bindTexture(gl.TEXTURE_2D, shiftTex)
        const x0 = x + dxCss, x1 = x0 + w
        const y0 = y, y1 = y + h
        ensureTexVerts(24)
        let o = 0
        // copyTexImage2D orientation: CSS top → v=1, CSS bottom → v=0
        texVerts[o++] = x0; texVerts[o++] = y0; texVerts[o++] = 0; texVerts[o++] = 1
        texVerts[o++] = x1; texVerts[o++] = y0; texVerts[o++] = 1; texVerts[o++] = 1
        texVerts[o++] = x0; texVerts[o++] = y1; texVerts[o++] = 0; texVerts[o++] = 0
        texVerts[o++] = x0; texVerts[o++] = y1; texVerts[o++] = 0; texVerts[o++] = 0
        texVerts[o++] = x1; texVerts[o++] = y0; texVerts[o++] = 1; texVerts[o++] = 1
        texVerts[o++] = x1; texVerts[o++] = y1; texVerts[o++] = 1; texVerts[o++] = 0
        gl.bufferData(gl.ARRAY_BUFFER, texVerts.subarray(0, o), gl.DYNAMIC_DRAW)
        gl.drawArrays(gl.TRIANGLES, 0, 6)
        gl.disable(gl.SCISSOR_TEST)
        if (blendOn) {
          gl.enable(gl.BLEND)
          gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
        }
        return true
      } catch (_) {
        try { gl.disable(gl.SCISSOR_TEST) } catch (_) {}
        return false
      }
    },

    /** Viewport + clear default FB once per shared scene frame (keeps blend). */
    beginFrame() {
      if (dead || !gl) return
      heatPassActive = false
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.viewport(0, 0, canvas.width, canvas.height)
      gl.clearColor(0, 0, 0, 0)
      gl.clear(gl.COLOR_BUFFER_BIT)
    },

    getContext() { return dead ? null : gl },

    /**
     * Move the view without touching a single vertex.
     *
     * p = (a_pos - anchor) * scale + anchor + translate,  all CSS px.
     *
     * A PAN is translate alone: two floats per frame, no buffer traffic, the
     * same cost at 200 cells or 200,000. Leave scale at 1 and the sprite path
     * stays pixel-exact, so text does not soften — the whole quality
     * trade-off lives on scale, and a caller can pan through this while still
     * rebuilding for zoom.
     *
     * The vertex buffer only needs rewriting when the DATA changes, or when a
     * pan runs past whatever off-screen margin the caller uploaded. Upload a
     * window wider than the viewport and the rebuild is amortised, not
     * per-frame.
     */
    setViewTransform(t) {
      xform.tx = Number.isFinite(t?.tx) ? t.tx : 0
      xform.ty = Number.isFinite(t?.ty) ? t.ty : 0
      xform.sx = Number.isFinite(t?.sx) && t.sx !== 0 ? t.sx : 1
      xform.sy = Number.isFinite(t?.sy) && t.sy !== 0 ? t.sy : 1
      xform.ax = Number.isFinite(t?.ax) ? t.ax : 0
      xform.ay = Number.isFinite(t?.ay) ? t.ay : 0
    },
    /** Back to identity. Call before any pass that uploads final-screen px. */
    resetViewTransform() {
      xform.tx = 0; xform.ty = 0; xform.sx = 1; xform.sy = 1; xform.ax = 0; xform.ay = 0
    },
    getViewTransform() { return { ...xform } },

    get hasHeatSnapshot() { return !dead && heatReady && !!heatTex },

    /**
     * Bind heat FBO (no clear — caller owns wipes). drawColorBatches land in snapshot.
     * @returns {boolean}
     */
    beginHeatLayer() {
      if (dead || !gl) return false
      if (!ensureHeatTarget(canvas.width, canvas.height)) return false
      heatPassActive = true
      gl.bindFramebuffer(gl.FRAMEBUFFER, heatFbo)
      gl.viewport(0, 0, heatDw, heatDh)
      // No clear here — LiquidityHeatLayer's histDirty/canBlit clear block
      // owns what to wipe so the flow band can shiftCssRect prior content.
      return true
    },

    /** Unbind heat FBO; mark snapshot ready. */
    commitHeatLayer() {
      if (dead || !gl) return
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.viewport(0, 0, canvas.width, canvas.height)
      if (heatPassActive && heatTex) heatReady = true
      heatPassActive = false
    },

    /**
     * Stamp a CSS-sized 2D canvas (labels) into the active heat FBO so text
     * rides the same blit as heat bars. Call between beginHeatLayer and
     * commitHeatLayer. source is CSS px matching the chart (same as fills).
     */
    stampHeatOverlay(sourceCanvas) {
      if (dead || !gl || !texProg || !heatPassActive || !sourceCanvas) return false
      if (!heatFbo || !heatDw || !heatDh) return false
      let tex = null
      try {
        tex = gl.createTexture()
        if (!tex) return false
        gl.bindFramebuffer(gl.FRAMEBUFFER, heatFbo)
        gl.viewport(0, 0, heatDw, heatDh)
        gl.disable(gl.DEPTH_TEST)
        gl.enable(gl.BLEND)
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
        gl.useProgram(texProg)
        gl.bindBuffer(gl.ARRAY_BUFFER, texBuf)
        gl.enableVertexAttribArray(aTexPos)
        gl.enableVertexAttribArray(aTexUv)
        gl.vertexAttribPointer(aTexPos, 2, gl.FLOAT, false, 16, 0)
        gl.vertexAttribPointer(aTexUv, 2, gl.FLOAT, false, 16, 8)
        if (aPos >= 0 && aPos !== aTexPos && aPos !== aTexUv) gl.disableVertexAttribArray(aPos)
        gl.uniform2f(uTexRes, cssW || 1, cssH || 1)
        applyXform(uTexTranslate, uTexScale, uTexAnchor)
        gl.uniform1i(uTex, 0)
        gl.uniform1f(uTexAlpha, 1)
        gl.activeTexture(gl.TEXTURE0)
        gl.bindTexture(gl.TEXTURE_2D, tex)
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 1)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
        const w = cssW || 1, h = cssH || 1
        ensureTexVerts(24)
        let o = 0
        // FLIP_Y upload: canvas top → high v
        texVerts[o++] = 0; texVerts[o++] = 0; texVerts[o++] = 0; texVerts[o++] = 1
        texVerts[o++] = w; texVerts[o++] = 0; texVerts[o++] = 1; texVerts[o++] = 1
        texVerts[o++] = 0; texVerts[o++] = h; texVerts[o++] = 0; texVerts[o++] = 0
        texVerts[o++] = 0; texVerts[o++] = h; texVerts[o++] = 0; texVerts[o++] = 0
        texVerts[o++] = w; texVerts[o++] = 0; texVerts[o++] = 1; texVerts[o++] = 1
        texVerts[o++] = w; texVerts[o++] = h; texVerts[o++] = 1; texVerts[o++] = 0
        gl.bufferData(gl.ARRAY_BUFFER, texVerts.subarray(0, o), gl.DYNAMIC_DRAW)
        gl.drawArrays(gl.TRIANGLES, 0, 6)
        return true
      } catch (_) {
        return false
      } finally {
        try { if (tex) gl.deleteTexture(tex) } catch (_) {}
      }
    },

    /**
     * Full-viewport blit of last heat snapshot onto the default framebuffer.
     * Uses the M3 tex program (premul blend).
     * @param {number} [dxCss=0] pan delta in CSS px — shift snapshot with the
     *   footprint during drag without rebuilding (clears made heat vanish).
     * @param {number} [scaleY=1] paint-space Y camera scale (same as FPCC ctx)
     * @param {number} [tyCss=0] paint-space Y camera translate
     */
    blitHeatLayer(dxCss, scaleY, tyCss) {
      if (dead || !gl || !texProg || !heatReady || !heatTex) return false
      heatPassActive = false
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.viewport(0, 0, canvas.width, canvas.height)
      gl.disable(gl.DEPTH_TEST)
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
      gl.useProgram(texProg)
      gl.bindBuffer(gl.ARRAY_BUFFER, texBuf)
      gl.enableVertexAttribArray(aTexPos)
      gl.enableVertexAttribArray(aTexUv)
      gl.vertexAttribPointer(aTexPos, 2, gl.FLOAT, false, 16, 0)
      gl.vertexAttribPointer(aTexUv, 2, gl.FLOAT, false, 16, 8)
      if (aPos >= 0 && aPos !== aTexPos && aPos !== aTexUv) gl.disableVertexAttribArray(aPos)
      gl.uniform2f(uTexRes, cssW || 1, cssH || 1)
      applyXform(uTexTranslate, uTexScale, uTexAnchor)
      gl.uniform1i(uTex, 0)
      gl.uniform1f(uTexAlpha, 1)
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(gl.TEXTURE_2D, heatTex)
      // FBO was rendered with same CSS→clip mapping: CSS top → tex v=1.
      const w = cssW || 1, h = cssH || 1
      const dx = Number.isFinite(dxCss) ? dxCss : 0
      const sy = Number.isFinite(scaleY) && scaleY > 0 ? scaleY : 1
      const ty = Number.isFinite(tyCss) ? tyCss : 0
      // Match FPCC 2D camera: y' = y * scaleY + ty (origin top-left).
      const y0 = ty
      const y1 = h * sy + ty
      ensureTexVerts(24)
      let o = 0
      // (0,0)→(0,1), (w,0)→(1,1), (0,h)→(0,0), (w,h)→(1,0) — pan + Y camera
      texVerts[o++] = 0 + dx; texVerts[o++] = y0; texVerts[o++] = 0; texVerts[o++] = 1
      texVerts[o++] = w + dx; texVerts[o++] = y0; texVerts[o++] = 1; texVerts[o++] = 1
      texVerts[o++] = 0 + dx; texVerts[o++] = y1; texVerts[o++] = 0; texVerts[o++] = 0
      texVerts[o++] = 0 + dx; texVerts[o++] = y1; texVerts[o++] = 0; texVerts[o++] = 0
      texVerts[o++] = w + dx; texVerts[o++] = y0; texVerts[o++] = 1; texVerts[o++] = 1
      texVerts[o++] = w + dx; texVerts[o++] = y1; texVerts[o++] = 1; texVerts[o++] = 0
      gl.bufferData(gl.ARRAY_BUFFER, texVerts.subarray(0, o), gl.DYNAMIC_DRAW)
      gl.drawArrays(gl.TRIANGLES, 0, 6)
      return true
    },

    /**
     * @param {Map<string, number[]>} fillMap flat [x,y,w,h,...] CSS px
     * @param {number} alpha LOD cell alpha 0..1
     * @returns {boolean} false if dead / no-op
     */
    drawColorBatches(fillMap, alpha) {
      if (dead || !gl) return false
      if (!fillMap || fillMap.size === 0) return true

      const aMul = alpha == null ? 1 : alpha
      beginSolidPass()
      // Stage every colour, upload once, then draw each at its offset.
      for (const [color, q] of fillMap) {
        pushSolidRects(color, q, aMul)
      }
      flushSolid()
      return true
    },

    /** Alias — heat fills use the same solid batch path. */
    drawHeatBatches(fillMap, alpha) {
      return controller.drawColorBatches(fillMap, alpha)
    },

    /**
     * M2 — historical OHLCV + candle-mode wicks/bodies.
     * Stride 9: [wx, yHigh, yLow, wickCol, bodyX, bodyTop, bodyW, bodyH, bodyColor]
     * WebGL1 lineWidth is often 1 only, so wicks are thin quads:
     *   outline width 3 (#000), core width 1 (wickCol), then body fill.
     * Alpha is always full strength (no cell LOD fade).
     * @param {Array} wb
     * @returns {boolean} false if dead; true if drawn (incl. empty)
     */
    drawWickBatch(wb) {
      if (dead || !gl) return false
      if (!wb || wb.length === 0) return true

      const stride = 9
      const candles = (wb.length / stride) | 0
      if (candles === 0) return true

      beginSolidPass()

      // Pass 1: black outline quads (width 3, centered on wx)
      const outline = []
      for (let i = 0; i < candles * stride; i += stride) {
        const wx = wb[i]
        const yH = wb[i + 1]
        const yL = wb[i + 2]
        const y0 = yH < yL ? yH : yL
        const hh = Math.abs(yL - yH)
        // (wick) Zero-length wick = tight stick (FPCC pushWickStick): the
        // stick is the body rect, so no halo and no core here.
        if (!(hh > 0)) continue
        outline.push(wx - 1.5, y0, 3, hh)
      }
      pushSolidRects('#000', outline, 1)

      // Pass 2: core wicks grouped by wickCol (width 1)
      for (const [, q] of wickCoreMap) q.length = 0
      for (let i = 0; i < candles * stride; i += stride) {
        const wx = wb[i]
        const yH = wb[i + 1]
        const yL = wb[i + 2]
        const col = wb[i + 3]
        const y0 = yH < yL ? yH : yL
        const hh = Math.abs(yL - yH)
        if (!(hh > 0)) continue
        let q = wickCoreMap.get(col)
        if (!q) { q = []; wickCoreMap.set(col, q) }
        q.push(wx - 0.5, y0, 1, hh)
      }
      for (const [color, q] of wickCoreMap) {
        if (q.length) pushSolidRects(color, q, 1)
      }

      // Pass 3: bodies grouped by bodyColor
      for (const [, q] of wickBodyMap) q.length = 0
      for (let i = 0; i < candles * stride; i += stride) {
        const bx = wb[i + 4]
        const by = wb[i + 5]
        const bw = wb[i + 6]
        const bh = wb[i + 7]
        const col = wb[i + 8]
        let q = wickBodyMap.get(col)
        if (!q) { q = []; wickBodyMap.set(col, q) }
        q.push(bx, by, bw, bh)
      }
      for (const [color, q] of wickBodyMap) {
        if (q.length) pushSolidRects(color, q, 1)
      }
      // All three wick passes ride one upload. Order is preserved by the
      // group offsets, so outlines still draw under cores under bodies.
      flushSolid()

      return true
    },

    /**
     * M3 — atlas cell-volume text sprites.
     * Stride 5: [sprite, dx, dy, dw, dh, ...] — dest size may scale away from
     * the atlas raster (CELL_ATLAS_PX); source is still sp.sx/sy/sw/sh.
     * Runs after fills/wicks on the same GL canvas (no clear between).
     * @param {Array} sb
     * @param {number} alpha LOD cell alpha 0..1
     * @returns {boolean} false if dead; true if ok (incl. empty)
     */
    drawSpriteBatch(sb, alpha) {
      if (dead || !gl) return false
      if (!texProg) return false
      if (!sb || sb.length === 0) return true

      const aMul = alpha == null ? 1 : alpha
      const ATLAS = 1024

      // Group by page canvas for fewer binds (local Map — no cross-frame retain)
      const groups = new Map()
      for (let i = 0; i < sb.length; i += 5) {
        const sp = sb[i]
        if (!sp || !sp.img || sp.sx == null || sp.sy == null || sp.sw == null || sp.sh == null) continue
        const dw = sb[i + 3], dh = sb[i + 4]
        if (!(dw > 0) || !(dh > 0)) continue
        const img = sp.img
        let idxs = groups.get(img)
        if (!idxs) { idxs = []; groups.set(img, idxs) }
        idxs.push(i)
      }

      // Resolve all page textures first — if any fail, fall back to 2D (no partial).
      const pageTex = []
      for (const [img, idxs] of groups) {
        if (!idxs.length) continue
        const tex = ensurePageTexture(img)
        if (!tex) return false
        pageTex.push([img, idxs, tex])
      }
      if (pageTex.length === 0) return true

      gl.disable(gl.DEPTH_TEST)
      gl.enable(gl.BLEND)
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
      gl.useProgram(texProg)
      gl.bindBuffer(gl.ARRAY_BUFFER, texBuf)
      gl.enableVertexAttribArray(aTexPos)
      gl.enableVertexAttribArray(aTexUv)
      // interleaved: x,y,u,v  stride 16
      gl.vertexAttribPointer(aTexPos, 2, gl.FLOAT, false, 16, 0)
      gl.vertexAttribPointer(aTexUv, 2, gl.FLOAT, false, 16, 8)
      gl.uniform2f(uTexRes, cssW || 1, cssH || 1)
      applyXform(uTexTranslate, uTexScale, uTexAnchor)
      gl.uniform1i(uTex, 0)
      gl.uniform1f(uTexAlpha, aMul)
      gl.activeTexture(gl.TEXTURE0)

      for (const [img, idxs, tex] of pageTex) {
        gl.bindTexture(gl.TEXTURE_2D, tex)

        const nSprites = idxs.length
        ensureTexVerts(nSprites * 24) // 6 verts * 4 floats
        let o = 0
        for (let k = 0; k < nSprites; k++) {
          const i = idxs[k]
          const sp = sb[i]
          const dx = sb[i + 1]
          const dy = sb[i + 2]
          const w = sb[i + 3]
          const h = sb[i + 4]
          const x2 = dx + w
          const y2 = dy + h
          const u0 = sp.sx / ATLAS
          const u1 = (sp.sx + sp.sw) / ATLAS
          // FLIP_Y upload: canvas top-left → high v
          const vTop = 1 - sp.sy / ATLAS
          const vBot = 1 - (sp.sy + sp.sh) / ATLAS

          // (dx,dy)→(u0,vTop), (dx+w,dy)→(u1,vTop), (dx,dy+h)→(u0,vBot)
          texVerts[o++] = dx; texVerts[o++] = dy; texVerts[o++] = u0; texVerts[o++] = vTop
          texVerts[o++] = x2; texVerts[o++] = dy; texVerts[o++] = u1; texVerts[o++] = vTop
          texVerts[o++] = dx; texVerts[o++] = y2; texVerts[o++] = u0; texVerts[o++] = vBot
          texVerts[o++] = dx; texVerts[o++] = y2; texVerts[o++] = u0; texVerts[o++] = vBot
          texVerts[o++] = x2; texVerts[o++] = dy; texVerts[o++] = u1; texVerts[o++] = vTop
          texVerts[o++] = x2; texVerts[o++] = y2; texVerts[o++] = u1; texVerts[o++] = vBot
        }
        gl.bufferData(gl.ARRAY_BUFFER, texVerts.subarray(0, o), gl.DYNAMIC_DRAW)
        gl.drawArrays(gl.TRIANGLES, 0, nSprites * 6)
      }

      return true
    },
  }

  return controller
}